import { Table, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import { sumBy } from "lodash";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
    PaymentMethod,
    Permission,
    Product,
    ProductObject,
    PurchaseItemObject,
    Source,
    SourceCopy,
    TransactionType,
} from "types";
import { ReservationistName } from "./ReservationistName";
import { Currency } from "@/components/Common/Currency";
import { InfiniteList } from "@/components/Common/InfiniteList";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { PaymentMethodIcon } from "@/components/Common/PaymentMethodIcon";
import { ProductImage } from "@/components/Common/ProductImage";
import { SourceIcon, SourceName } from "@/components/Common/SourceIcon";
import { useProducts } from "@/hooks/resources/useProduct";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useEarningReportParams } from "@/hooks/useEarningReportParams";
import { useResource } from "@/hooks/useResource";
import { isAmountIncludesPartnerFee, showPartnerFee } from "@/models/PartnerFeeFormula";
import { getPaymentMethodName } from "@/models/Payment";
import { getGuestFee, getMerchandiseFee, getPartnerFee, getProcessingFee } from "@/models/Transaction";

export const EarningsReportTransactionsList = () => {
    const seller = useSeller();
    const history = useHistory();
    const { products, isFetching } = useProducts();
    const { isGranted } = useAuthorization();
    const showPurchases = isGranted(Permission.PURCHASES_VIEW);
    const { transactions, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage } = useEarningReportParams();

    const showGuestFee = useMemo(() => {
        return isAmountIncludesPartnerFee(seller.partnerFeeFormulas);
    }, [seller]);

    const showMerchandiseFee = isGranted(Permission.MERCHANDISE_VIEW);

    const handleRowClick = (id?: ID) => {
        if (!showPurchases || !id) {
            return;
        }

        return history.push(`/purchases/${id}`);
    };

    if (!transactions) {
        return null;
    }

    return (
        <InfiniteList
            hasMore={hasNextPage}
            isLoading={isLoading || isFetchingNextPage || isFetching}
            onLoadMore={fetchNextPage}
        >
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header>Item</Table.Header>
                        <Table.Header>Date</Table.Header>
                        <Table.Header>Reservationist</Table.Header>
                        <Table.Header>Gross</Table.Header>
                        <Table.Header>Processing Fee</Table.Header>
                        {showPartnerFee() && <Table.Header>Service Fee</Table.Header>}
                        {showGuestFee && <Table.Header>Guest Fee</Table.Header>}
                        {showMerchandiseFee && <Table.Header>Merchandise Fee</Table.Header>}
                        <Table.Header>Taxes/Fees</Table.Header>
                        <Table.Header>Net</Table.Header>
                        <Table.Header>Transaction Source</Table.Header>
                        <Table.Header>Method</Table.Header>
                    </Table.Row>
                </Table.Head>

                <Table.Body isStriped>
                    {transactions.map((transaction) => {
                        const { items } = transaction;
                        const taxFee = sumBy(items, "taxFee");
                        const gross = sumBy(items, "gross");
                        const net = sumBy(items, "net");
                        const [purchaseItem] = transaction.items;
                        const product = purchaseItem?.orderItem?.product;
                        const productDetails = products.find((p) => product?.id === p.id);

                        const isStripePaydown = transaction.type === TransactionType.STRIPE_PAYDOWN;
                        const showGiftIcon =
                            !isStripePaydown &&
                            (transaction.source === Source.GIFT ||
                                productDetails?.object === ProductObject.GIFT_OFFER ||
                                !product ||
                                productDetails?.name === "Choose an amount");
                        const showStoreCreditIcon = transaction.method === PaymentMethod.STORE_CREDIT && !product;
                        const itemObject = showStoreCreditIcon
                            ? PurchaseItemObject.STORE_CREDIT
                            : showGiftIcon
                            ? PurchaseItemObject.GIFT
                            : PurchaseItemObject.EXPERIENCE;

                        return (
                            <Table.Row
                                key={transaction.id}
                                className={clsx("w-24 pr-0", showPurchases && "cursor-pointer")}
                                onClick={() => handleRowClick(transaction.purchase?.id)}
                            >
                                <Table.Cell>
                                    <Tooltip disabled={!product} content={<ListingNameTooltip product={product} />}>
                                        <ProductImage
                                            product={productDetails ?? product}
                                            size="small"
                                            object={itemObject}
                                        />
                                    </Tooltip>
                                </Table.Cell>
                                <Table.Cell className="no-translate">
                                    {formatDate(transaction.createdAt, DateFormat.DATE_SHORT)}
                                </Table.Cell>
                                <Table.Cell>
                                    <ReservationistName
                                        id={transaction.reservationist.id}
                                        name={transaction.reservationist.name}
                                    />
                                </Table.Cell>
                                <Table.Cell>
                                    <FeeWithCurrency amount={gross} />
                                </Table.Cell>
                                <Table.Cell>
                                    <FeeWithCurrency amount={getProcessingFee(transaction)} />
                                </Table.Cell>
                                {showPartnerFee() && (
                                    <Table.Cell>
                                        <FeeWithCurrency amount={getPartnerFee(transaction)} />
                                    </Table.Cell>
                                )}
                                {showGuestFee && (
                                    <Table.Cell>
                                        <FeeWithCurrency amount={getGuestFee(transaction)} />
                                    </Table.Cell>
                                )}
                                {showMerchandiseFee && (
                                    <Table.Cell>
                                        <FeeWithCurrency amount={getMerchandiseFee(transaction)} />
                                    </Table.Cell>
                                )}
                                <Table.Cell>
                                    <FeeWithCurrency amount={taxFee} />
                                </Table.Cell>
                                <Table.Cell>
                                    <FeeWithCurrency amount={net} />
                                </Table.Cell>
                                <Table.Cell>
                                    {transaction.source && (
                                        <>
                                            <SourceIcon
                                                source={transaction.source}
                                                size="small"
                                                className="top-0 mr-2"
                                            />
                                            {SourceCopy[transaction.source] ?? (
                                                <SourceName source={transaction.source} />
                                            )}
                                        </>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <>
                                        <PaymentMethodIcon
                                            method={transaction.method}
                                            size="small"
                                            className="top-0 mr-2"
                                        />

                                        {getPaymentMethodName(seller, transaction.method) ?? transaction.method}
                                    </>
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>
        </InfiniteList>
    );
};

const ListingNameTooltip = ({ product }: { product: Product }) => {
    const { data: experience } = useResource<Product>(["products", product?.id]);
    return <NoTranslate as="div">{experience?.name}</NoTranslate>;
};

const FeeWithCurrency = ({ amount }: { amount: number }) => {
    return (
        <div className={clsx(amount < 0 ? "text-red" : "text-black")}>
            <Currency>{amount}</Currency>
        </div>
    );
};
